import React, { useEffect, useState } from 'react';
import { apiFetch, fmt } from '../utils/api';
import Topbar from '../components/Topbar';
import { EmptyState, Spinner, useToast, Modal } from '../components/UI';
import * as XLSX from 'xlsx';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function parsePL(data) {
  const wb = XLSX.read(data, { type: 'array', raw: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true });
  let totalIncome = 0, totalCosts = 0, inIncome = false, inCosts = false;
  rows.forEach(row => {
    const label = String(row[0]||'').trim().toLowerCase();
    if (label.includes('turnover') || label.includes('income') || label.includes('revenue')) { inIncome = true; inCosts = false; return; }
    if (label.includes('administrative') || label.includes('expenses') || label.includes('costs')) { inCosts = true; inIncome = false; return; }
    if (label.includes('gross profit') || label.includes('net profit') || label.includes('total income') || label.includes('total costs')) return;
    const val = parseFloat(row[1]) || parseFloat(row[2]) || 0;
    if (inIncome && val) totalIncome += Math.abs(val);
    if (inCosts && val) totalCosts += Math.abs(val);
  });
  return { totalIncome, totalCosts, netProfit: totalIncome - totalCosts };
}

export default function CFODashboard({ user }) {
  const [tab, setTab] = useState('overview');
  const [directors, setDirectors] = useState([]);
  const [allPL, setAllPL] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selYear, setSelYear] = useState(new Date().getFullYear());
  const [importFile, setImportFile] = useState(null);
  const [importPeriod, setImportPeriod] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showImportedPeriods, setShowImportedPeriods] = useState(false);
  const [editDirectors, setEditDirectors] = useState(false);
  const [dirEdits, setDirEdits] = useState([]);
  const [showToast, Toast] = useToast();
  const isAdmin = user?.role === 'admin';

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    const [dirs, pls] = await Promise.all([apiFetch('/cfo/directors'), apiFetch('/cfo/pl-imports')]);
    setDirectors(dirs || []);
    setAllPL(pls || []);
    setLoading(false);
  }

  async function doImport() {
    if (!importPeriod || !importPreview) return;
    setImporting(true);
    await apiFetch('/cfo/pl-import', { method: 'POST', body: JSON.stringify({ period: importPeriod, data: importPreview }) });
    await apiFetch('/api/activity', { method: 'POST', body: JSON.stringify({ action: 'Uploaded P&L', details: `Period: ${importPeriod} — Income: ${fmt(importPreview.totalIncome)}, Costs: ${fmt(importPreview.totalCosts)}, Net: ${fmt(importPreview.netProfit)}` }) });
    showToast(`Saved P&L for ${importPeriod}`);
    setImporting(false);
    setImportFile(null);
    setImportPreview(null);
    setImportPeriod('');
    loadAll();
  }

  async function saveDirectors() {
    const total = dirEdits.reduce((s, d) => s + parseFloat(d.share_percent||0), 0);
    if (Math.abs(total - 98) > 0.01) { showToast('Shares must add up to 98%'); return; }
    const changes = dirEdits.map((d, i) => {
      const orig = directors[i];
      return orig && parseFloat(orig.share_percent) !== parseFloat(d.share_percent) ? `${d.name}: ${orig.share_percent}% → ${d.share_percent}%` : null;
    }).filter(Boolean);
    await apiFetch('/cfo/directors', { method: 'PUT', body: JSON.stringify({ directors: dirEdits }) });
    if (changes.length) await apiFetch('/activity', { method: 'POST', body: JSON.stringify({ action: 'Updated director shares', details: changes.join('; ') }) });
    showToast('Shares updated');
    setEditDirectors(false);
    loadAll();
  }

  // Get current month's P&L
  const currentPL = (() => {
    if (!allPL.length) return null;
    const sorted = [...allPL].sort((a, b) => {
      const [am, ay] = a.period.split(' '); const [bm, by] = b.period.split(' ');
      const aDate = new Date(`${am} 1, ${ay}`); const bDate = new Date(`${bm} 1, ${by}`);
      return bDate - aDate;
    });
    return sorted[0];
  })();

  const currentData = currentPL ? (typeof currentPL.data === 'string' ? JSON.parse(currentPL.data) : currentPL.data) : null;

  function calcQuarterly() {
    const fyQuarters = {
      Q1: { label: `Q1 Apr–Jun ${selYear}`, months: ['Apr','May','Jun'], year: selYear, income: 0, costs: 0, net: 0 },
      Q2: { label: `Q2 Jul–Sep ${selYear}`, months: ['Jul','Aug','Sep'], year: selYear, income: 0, costs: 0, net: 0 },
      Q3: { label: `Q3 Oct–Dec ${selYear}`, months: ['Oct','Nov','Dec'], year: selYear, income: 0, costs: 0, net: 0 },
      Q4: { label: `Q4 Jan–Mar ${selYear+1}`, months: ['Jan','Feb','Mar'], year: selYear+1, income: 0, costs: 0, net: 0 },
    };
    allPL.forEach(row => {
      const [mon, yr] = row.period.split(' ');
      const data = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
      Object.keys(fyQuarters).forEach(k => {
        const q = fyQuarters[k];
        if (q.months.includes(mon) && parseInt(yr) === q.year) {
          q.income += data.totalIncome||0;
          q.costs += data.totalCosts||0;
          q.net += data.netProfit||0;
        }
      });
    });
    return fyQuarters;
  }

  if (loading) return <div style={{ marginLeft: 'var(--sidebar-width)' }}><Topbar title="CFO Dashboard" /><Spinner /></div>;

  const quarters = calcQuarterly();

  return (
    <div style={{ marginLeft: 'var(--sidebar-width)', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Topbar title="CFO Dashboard" subtitle="Director financials — confidential" />
      {Toast}

      {/* Tabs */}
      <div style={{ display: 'flex', background: '#fff', borderBottom: '1px solid var(--border)', padding: '0 24px' }}>
        {[['overview','Overview'],['pl','P&L'],['directors','Directors'],['quarterly','Quarterly']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '12px 16px', fontSize: 13, fontWeight: tab === key ? 500 : 400,
            color: tab === key ? 'var(--navy)' : 'var(--text-tertiary)',
            background: 'none', border: 'none', borderBottom: `2px solid ${tab === key ? 'var(--navy)' : 'transparent'}`,
            cursor: 'pointer', marginBottom: -1, fontFamily: 'inherit'
          }}>{label}</button>
        ))}
      </div>

      <div style={{ padding: 24 }}>
        {tab === 'overview' && (
          <div>
            {!currentData ? (
              <div className="card" style={{ padding: 48, textAlign: 'center' }}>
                <EmptyState icon="ti-upload" message="Upload monthly P&L files to see financial overview" />
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-tertiary)' }}>Latest period: {currentPL.period}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
                  {[
                    { label: 'Total Income', value: fmt(currentData.totalIncome), color: 'var(--blue)' },
                    { label: 'Total Costs', value: fmt(currentData.totalCosts), color: 'var(--red)' },
                    { label: 'Net Profit', value: fmt(currentData.netProfit), color: currentData.netProfit >= 0 ? 'var(--green)' : 'var(--red)' },
                  ].map(m => (
                    <div key={m.label} className="card" style={{ padding: '14px 16px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>{m.label}</div>
                      <div style={{ fontSize: 24, fontWeight: 600, color: m.color, fontFamily: 'DM Mono, monospace' }}>{m.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                  <div className="card" style={{ padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>Charity (2%)</div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--navy)', fontFamily: 'DM Mono, monospace' }}>{fmt(currentData.netProfit * 0.02)}</div>
                  </div>
                  <div className="card" style={{ padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>Distributable (98%)</div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--green)', fontFamily: 'DM Mono, monospace' }}>{fmt(currentData.netProfit * 0.98)}</div>
                  </div>
                  <div className="card" style={{ padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>Periods imported</div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--navy)' }}>{allPL.length}</div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'pl' && (
          <div>
            {isAdmin && (
              <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Upload P&L from Xero</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label>PERIOD (e.g. Apr 2026)</label>
                    <input value={importPeriod} onChange={e => setImportPeriod(e.target.value)} placeholder="Apr 2026" />
                  </div>
                  <div>
                    <label>XERO P&L FILE (.xlsx)</label>
                    <input type="file" accept=".xlsx,.xls" onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setImportFile(file);
                      const reader = new FileReader();
                      reader.onload = ev => {
                        try { setImportPreview(parsePL(new Uint8Array(ev.target.result))); } catch { showToast('Could not parse file'); }
                      };
                      reader.readAsArrayBuffer(file);
                    }} style={{ padding: '6px 0' }} />
                  </div>
                  <button className="btn btn-primary" onClick={doImport} disabled={!importPreview || !importPeriod || importing}>
                    {importing ? 'Saving...' : 'Save P&L'}
                  </button>
                </div>
                {importPreview && (
                  <div style={{ marginTop: 10, padding: '10px 14px', background: 'var(--blue-bg)', borderRadius: 'var(--r-md)', display: 'flex', gap: 16, fontSize: 12 }}>
                    <span>Income: <strong>{fmt(importPreview.totalIncome)}</strong></span>
                    <span>Costs: <strong>{fmt(importPreview.totalCosts)}</strong></span>
                    <span>Net: <strong style={{ color: importPreview.netProfit >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(importPreview.netProfit)}</strong></span>
                  </div>
                )}
              </div>
            )}
            <div className="card" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>Imported Periods ({allPL.length})</div>
              {allPL.length === 0 ? <EmptyState icon="ti-upload" message="No P&L files uploaded yet" /> : (
                <table className="table">
                  <thead><tr><th>PERIOD</th><th>INCOME</th><th>COSTS</th><th>NET PROFIT</th>{isAdmin && <th></th>}</tr></thead>
                  <tbody>
                    {[...allPL].sort((a, b) => {
                      const [am, ay] = a.period.split(' '); const [bm, by] = b.period.split(' ');
                      return new Date(`${bm} 1, ${by}`) - new Date(`${am} 1, ${ay}`);
                    }).map(p => {
                      const d = typeof p.data === 'string' ? JSON.parse(p.data) : p.data;
                      return (
                        <tr key={p.period}>
                          <td style={{ fontWeight: 500 }}>{p.period}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace' }}>{fmt(d.totalIncome)}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace', color: 'var(--red)' }}>{fmt(d.totalCosts)}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, color: d.netProfit >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(d.netProfit)}</td>
                          {isAdmin && <td><button className="btn btn-sm btn-danger" onClick={async () => { await apiFetch(`/cfo/pl-import/${encodeURIComponent(p.period)}`, { method: 'DELETE' }); loadAll(); }}>Delete</button></td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === 'directors' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3>Director Profit Shares</h3>
              {isAdmin && !editDirectors && <button className="btn btn-secondary btn-sm" onClick={() => { setDirEdits(directors.map(d => ({ ...d }))); setEditDirectors(true); }}>Edit shares</button>}
              {editDirectors && <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditDirectors(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={saveDirectors}>Save</button>
              </div>}
            </div>
            {currentData && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
                <div className="card" style={{ padding: '12px 16px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>Net profit ({currentPL?.period})</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--navy)', fontFamily: 'DM Mono, monospace' }}>{fmt(currentData.netProfit)}</div>
                </div>
                <div className="card" style={{ padding: '12px 16px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>Charity (2%)</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--blue)', fontFamily: 'DM Mono, monospace' }}>-{fmt(currentData.netProfit * 0.02)}</div>
                </div>
                <div className="card" style={{ padding: '12px 16px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>Distributable</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--green)', fontFamily: 'DM Mono, monospace' }}>{fmt(currentData.netProfit * 0.98)}</div>
                </div>
              </div>
            )}
            <div className="card" style={{ overflow: 'hidden' }}>
              <table className="table">
                <thead><tr><th>DIRECTOR</th><th>SHARE %</th>{currentData && <th>THIS MONTH</th>}</tr></thead>
                <tbody>
                  {(editDirectors ? dirEdits : directors).map((d, i) => (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 500 }}>{d.name}</td>
                      <td>
                        {editDirectors
                          ? <input type="number" value={dirEdits[i]?.share_percent} onChange={e => { const arr = [...dirEdits]; arr[i] = { ...arr[i], share_percent: e.target.value }; setDirEdits(arr); }} style={{ width: 80 }} />
                          : <span>{d.share_percent}%</span>
                        }
                      </td>
                      {currentData && <td style={{ fontFamily: 'DM Mono, monospace', color: 'var(--green)', fontWeight: 500 }}>{fmt(currentData.netProfit * 0.98 * (parseFloat(d.share_percent)/100))}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'quarterly' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <label style={{ marginBottom: 0 }}>FINANCIAL YEAR</label>
              <select value={selYear} onChange={e => setSelYear(parseInt(e.target.value))} style={{ width: 'auto' }}>
                {[2024,2025,2026,2027].map(y => <option key={y} value={y}>Apr {y} – Mar {y+1}</option>)}
              </select>
            </div>
            {allPL.length === 0 ? <EmptyState icon="ti-upload" message="Upload P&L files to see quarterly view" /> : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 12 }}>
                  {Object.values(quarters).map(q => {
                    const charity = q.net * 0.02;
                    const dist = q.net * 0.98;
                    return (
                      <div key={q.label} className="card" style={{ padding: 16 }}>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8, fontWeight: 500 }}>{q.label}</div>
                        <div style={{ fontSize: 11, marginBottom: 3 }}>Income: <span style={{ color: 'var(--blue)', fontWeight: 500 }}>{fmt(q.income)}</span></div>
                        <div style={{ fontSize: 11, marginBottom: 3 }}>Costs: <span style={{ color: 'var(--red)' }}>{fmt(q.costs)}</span></div>
                        <div style={{ fontSize: 11, marginBottom: 6 }}>Charity (2%): <span style={{ color: 'var(--navy)' }}>-{fmt(charity)}</span></div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: dist >= 0 ? 'var(--green)' : 'var(--red)', fontFamily: 'DM Mono, monospace' }}>{fmt(dist)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>distributable</div>
                      </div>
                    );
                  })}
                </div>
                <div className="card" style={{ overflow: 'hidden', marginBottom: 12 }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>DIRECTOR</th><th>SHARE</th>
                        {Object.values(quarters).map(q => <th key={q.label}>{q.label.split(' ')[0]} {q.label.split(' ')[2]}</th>)}
                        <th>ANNUAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {directors.map(d => {
                        let annual = 0;
                        const qShares = Object.values(quarters).map(q => {
                          const s = q.net * 0.98 * (parseFloat(d.share_percent)/100);
                          annual += s; return s;
                        });
                        return (
                          <tr key={d.id}>
                            <td style={{ fontWeight: 500 }}>{d.name}</td>
                            <td style={{ color: 'var(--text-secondary)' }}>{d.share_percent}%</td>
                            {qShares.map((s, i) => <td key={i} style={{ fontFamily: 'DM Mono, monospace', color: s >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(s)}</td>)}
                            <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 700, color: annual >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(annual)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
